import { atlasBust, loadAtlasMeta, type AtlasMeta, type SpriteGroup } from './atlas.js';

/**
 * Sprites for the DOM portraits (hunt lists, prey, outfit picker...).
 *
 * The full creature/outfit atlases hold every walking frame in four
 * directions (~40 + ~90 pages of 2048²), so a hunts window with a hundred
 * portraits used to pull ~14 MB before the last icon appeared. The extractor
 * now also writes small icon atlases (`tools/extractor/build_icon_atlas.py`:
 * idle, phase 0, facing south, every addon row + recolour mask; ~1.2 MB for
 * all creatures) which are prefetched while the player stands in town.
 * Anything outside that scope (other directions, mounted outfits) falls back
 * to the full atlas, page by page as before.
 */

export type IconKind = 'creatures' | 'outfits';

const SMALL: Record<IconKind, string> = {
  creatures: 'creature-icons',
  outfits: 'outfit-icons',
};

const smallMeta = new Map<IconKind, Promise<AtlasMeta | null>>();
const pageImages = new Map<string, Promise<HTMLImageElement>>();

function loadSmallMeta(kind: IconKind): Promise<AtlasMeta | null> {
  let promise = smallMeta.get(kind);
  if (!promise) {
    // Revalidate (cheap 304) so a regenerated atlas is never read with stale frames.
    promise = fetch(`/assets/${SMALL[kind]}.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? (r.json() as Promise<AtlasMeta>) : null))
      .catch(() => null);
    smallMeta.set(kind, promise);
  }
  return promise;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  const existing = pageImages.get(url);
  if (existing) return existing;
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`icon page ${url}`));
    image.src = url;
  });
  promise.catch(() => pageImages.delete(url));
  pageImages.set(url, promise);
  return promise;
}

const absentSets = new WeakMap<AtlasMeta, Set<number>>();

/** In-scope sprites the extractor found blank: nothing to fetch from the full atlas. */
function absentSet(meta: AtlasMeta): Set<number> {
  let set = absentSets.get(meta);
  if (!set) {
    set = new Set((meta.atlas as { absent?: number[] }).absent ?? []);
    absentSets.set(meta, set);
  }
  return set;
}

function pageUrl(meta: AtlasMeta, page: number): string | null {
  const file = meta.atlas.pages[page];
  return file ? `/assets/${file}?v=${atlasBust(meta)}` : null;
}

/** Idle group (or the first one) of an appearance, from the small atlas when it has it. */
export async function iconGroup(kind: IconKind, lookType: number): Promise<SpriteGroup | null> {
  const small = await loadSmallMeta(kind);
  const pick = (meta: AtlasMeta | null) => {
    const groups = meta?.entries[String(lookType)]?.groups;
    if (!groups) return null;
    return groups['0'] ?? Object.values(groups)[0] ?? null;
  };
  return pick(small) ?? pick(await loadAtlasMeta(kind).catch(() => null));
}

/** One sprite's pixels; small icon atlas first, full atlas page as fallback. */
export async function cropIconSprite(kind: IconKind, spriteId: number): Promise<ImageData | null> {
  const small = await loadSmallMeta(kind);
  let meta: AtlasMeta | null = small;
  let frame = small?.atlas.frames[String(spriteId)];
  if (!frame && small && absentSet(small).has(spriteId)) return null;
  if (!frame) {
    meta = await loadAtlasMeta(kind).catch(() => null);
    frame = meta?.atlas.frames[String(spriteId)];
  }
  if (!meta || !frame) return null;
  const [page, x, y, w, h] = frame;
  const url = pageUrl(meta, page);
  if (!url) return null;
  const image = await loadImage(url);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(image, x, y, w, h, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

/** One download per icon-atlas page (the "ENTERING WORLD" preload counts them). */
export async function iconAtlasPageTasks(): Promise<Array<() => Promise<unknown>>> {
  const tasks: Array<() => Promise<unknown>> = [];
  for (const kind of ['creatures', 'outfits'] as IconKind[]) {
    const meta = await loadSmallMeta(kind);
    if (!meta) continue;
    for (let page = 0; page < meta.atlas.pages.length; page += 1) {
      const url = pageUrl(meta, page);
      if (url) tasks.push(() => loadImage(url));
    }
  }
  return tasks;
}

let prefetchStarted = false;

/**
 * Download every icon page while the browser is idle (called once the game
 * screen is up), so opening the hunts window or the outfit picker finds the
 * portraits in memory. Pages load one at a time to stay out of the way of the
 * town's own sprite requests.
 */
export function prefetchIconAtlases(): void {
  if (prefetchStarted || typeof window === 'undefined') return;
  prefetchStarted = true;
  const idle = (fn: () => void) => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    if (ric) ric(fn, { timeout: 2500 });
    else window.setTimeout(fn, 800);
  };
  idle(() => {
    void (async () => {
      for (const kind of ['creatures', 'outfits'] as IconKind[]) {
        const meta = await loadSmallMeta(kind);
        if (!meta) continue;
        for (let page = 0; page < meta.atlas.pages.length; page += 1) {
          const url = pageUrl(meta, page);
          if (url) await loadImage(url).catch(() => undefined);
        }
      }
    })();
  });
}
